import { useCallback } from 'react';

import {
  addUploadToProject,
  alwaysCreateNewProject,
} from 'src/modules/homeownerApp/components/UploadImage';
import {
  EmbedKind,
  useEmbedContext,
} from 'src/modules/homeownerApp/providers/EmbedProvider';
import type { NewProjectArgs } from 'src/shared/graphql/generated';
import { usePublicOrgInfoLazyQuery } from 'src/shared/graphql/generated';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';

import { createAiOriginalImage } from '../services';

import { useHomeownerAppContext } from './useHomeownerAppContext';

export const useUploadOriginalImage = () => {
  const { setAffOrgPublicId, affOrgPublicId } = useGlobalStorageContext();
  const { embedKind, isEmbed } = useEmbedContext();
  const [publicOrgInfoQuery] = usePublicOrgInfoLazyQuery();
  const { trackingData } = useHomeownerAppContext();

  const clearAffOrgId = useCallback(() => {
    if (isEmbed) {
      return;
    }

    setAffOrgPublicId();
  }, [isEmbed, setAffOrgPublicId]);

  return useCallback(
    async (variables: {
      image: File;
      email?: string;
      spaceType?: string;
      marketingConsent?: string;
      jobClientIdentifier?: string | null;
      projectId?: number;
    }) => {
      const addToProject = addUploadToProject();
      const createNewProject = alwaysCreateNewProject();
      const isEmbedV1 = embedKind !== EmbedKind.RealtorPOC && isEmbed;
      let project: Partial<NewProjectArgs> | undefined;

      // do not pass in any project ids if we are creating a new project
      const projectId =
        (affOrgPublicId && !isEmbedV1) || createNewProject
          ? undefined
          : variables.projectId || addToProject?.id;

      // the isEmbedV1 check is temporary until we move Reator off v0 embed.
      // Until that happens, we don't want to create a project with the orgId for v0, but
      // we do for v1 embed.
      if ((!isEmbed || isEmbedV1) && affOrgPublicId) {
        const { data: publicOrgInfo } = await publicOrgInfoQuery({
          variables: { publicIdentifier: affOrgPublicId },
        });
        if (publicOrgInfo?.publicOrgInfo) {
          project = {};
          project.orgId = publicOrgInfo.publicOrgInfo.id;
        }
        clearAffOrgId();
      }

      const result = await createAiOriginalImage(
        variables.image,
        {
          email: variables.email,
          spaceType: variables.spaceType,
          marketingConsent: variables.marketingConsent,
          jobClientIdentifier: variables.jobClientIdentifier,
          project,
          projectId,
        },
        trackingData,
        true,
      );

      return result;
    },
    [
      embedKind,
      affOrgPublicId,
      isEmbed,
      trackingData,
      publicOrgInfoQuery,
      clearAffOrgId,
    ],
  );
};
